import { format, type Locale } from "date-fns";
import { enUS } from "date-fns/locale";
import { es } from "date-fns/locale";

const FALLBACK = "—";

/**
 * Parse a date-only string (YYYY-MM-DD or ISO with time) as local midnight.
 * Use for fiscal due dates and any date-only value so comparisons are correct
 * in all timezones (new Date("2026-02-24") is UTC midnight and shifts to
 * the previous day in timezones behind UTC when using setHours(0,0,0,0)).
 */
export function parseLocalDateOnly(isoDate: string): Date {
  const s = isoDate.slice(0, 10);
  const [y, m, d] = s.split("-").map(Number);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
    return new Date(isoDate);
  }
  return new Date(y, m - 1, d);
}

const DATE_ONLY_FORMAT = "d MMM yyyy";
const DATE_TIME_FORMAT = "d MMM yyyy, HH:mm";

const localeMap: Record<string, Locale> = {
  es,
  en: enUS,
};

/**
 * Has the calendar DAY of `value` already finished?
 *
 * For deadlines that are days, not instants. `now > value` marks a request
 * overdue from 00:01 on its own due date — reported from production: "la fecha
 * estimada de la solicitud es 14/09 pero entiendo que deberia aparecer como
 * atrasada recien cuando se cumpla el dia ya que dentro del mismo dia deberia
 * estar a tiempo".
 *
 * Compared in the LOCAL calendar frame, which is the frame `formatDisplayDate`
 * renders in — so the badge and the date beside it can never contradict each
 * other, which is what made the original report confusing rather than merely
 * early.
 *
 * `estimatedCompletionDate` is `timestamp without time zone` and holds three
 * shapes in production (midnight, a day shifted by an old timezone bug, and a
 * full instant — which is why the timestamptz migration deliberately left it
 * alone). This treats all three the same way: whatever day the UI shows is the
 * day it waits for. A row whose stored day is itself wrong stays wrong, and
 * that is a data problem, not something a badge should paper over.
 *
 * Unparseable input is NOT overdue: crying wolf on bad data is worse than
 * staying quiet.
 */
export function isPastCalendarDay(
  value: string | Date,
  now: Date = new Date(),
): boolean {
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return false;
  const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const todayStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  return todayStart > dayStart;
}

export function getDateFnsLocale(locale?: string): Locale {
  if (!locale) return es;
  const normalized = locale.split("-")[0].toLowerCase();
  return localeMap[normalized] ?? es;
}

/**
 * Lowercase the month segment in a formatted date string (e.g. "18 Feb 2026" → "18 feb 2026").
 */
function lowercaseMonth(formatted: string): string {
  const parts = formatted.split(" ");
  if (parts.length >= 2) {
    parts[1] = parts[1].toLowerCase();
    return parts.join(" ");
  }
  return formatted;
}

export interface FormatDisplayDateOptions {
  includeTime?: boolean;
  locale?: string;
  /**
   * When true and includeTime is false, format using the UTC calendar date
   * (year, month, day) so that date-only values like 2026-02-18T00:00:00Z
   * display as "18 feb 2026" in any timezone instead of shifting to the
   * previous/next local day. Use for fiscal due dates and other date-only fields.
   */
  useCalendarDate?: boolean;
}

/**
 * Formats a date for user-facing display: "18 feb 2026" or "18 feb 2026, 16:08".
 * Invalid dates return "—".
 */
export function formatDisplayDate(
  date: string | Date,
  options?: FormatDisplayDateOptions
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return FALLBACK;

  const locale = getDateFnsLocale(options?.locale);
  const pattern = options?.includeTime ? DATE_TIME_FORMAT : DATE_ONLY_FORMAT;

  let toFormat = d;
  if (!options?.includeTime && options?.useCalendarDate) {
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    const day = d.getUTCDate();
    toFormat = new Date(y, m, day);
  }
  const formatted = format(toFormat, pattern, { locale });
  return lowercaseMonth(formatted);
}
